import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import Dialog from "../../components/ui/Dialog";
import EmployeePicker from "../../components/ui/EmployeePicker";
import useCurrentUser from "../../hooks/useCurrentUser";
import { employeeName } from "../../utils/text";
import {
  useEmployeeCheckInSettings,
  useRoleCheckInSettings,
  useUpdateEmployeeCheckInSettings,
  useUpdateRoleCheckInSetting,
} from "../../api/queries/attendanceQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { fromNow, formatDateTime } from "../../utils/dateTime";
import "./attendance.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];

const CONSENT = {
  true: { label: "Allowed", tone: "green" },
  false: { label: "Declined", tone: "red" },
  null: { label: "Not asked", tone: "gray" },
};

// Per-employee rules (check in anywhere, multiple check-ins): follow the
// role, or force on / off whatever the role says
const MODE = {
  INHERIT: { label: "Role default", tone: "gray" },
  ALLOW: { label: "Allowed", tone: "green" },
  BLOCK: { label: "Blocked", tone: "red" },
};

/** mode = INHERIT | ALLOW | BLOCK (older servers: none → from the effective value); INHERIT shows the role's value. */
const ModeCell = ({ mode, effective }) => {
  const m = mode || (effective ? "ALLOW" : "INHERIT");
  const meta = MODE[m] || MODE.INHERIT;
  return (
    <span className={`status-pill status-pill--${meta.tone}`}>
      {meta.label}
      {m === "INHERIT" &&
        effective !== undefined &&
        `: ${effective ? "on" : "off"}`}
    </span>
  );
};

/** "Change to…" select for one rule, applied to the selected employees. */
const ModeSelect = ({ id, label, disabled, onPick }) => (
  <label
    htmlFor={id}
    className="fs-080 text-slate-600 d-flex align-items-center gap-2"
  >
    {label}:
    <select
      id={id}
      className="form-select form-select-sm w-auto"
      value=""
      disabled={disabled}
      onChange={(e) => e.target.value && onPick(e.target.value)}
    >
      <option value="">Change to…</option>
      <option value="INHERIT">Follow the role default</option>
      <option value="ALLOW">Allow (whatever the role)</option>
      <option value="BLOCK">Block (whatever the role)</option>
    </select>
  </label>
);
const MODE_DONE = {
  INHERIT: "follows the role",
  ALLOW: "allowed",
  BLOCK: "blocked",
};

/** One rule: No change / Role default / Allowed / Blocked. */
const RuleSelect = ({ id, label, value, onChange }) => (
  <div className="col-md-6">
    <label htmlFor={id} className="profile-label">
      {label}
    </label>
    <select
      id={id}
      className="form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">No change</option>
      <option value="INHERIT">Role default</option>
      <option value="ALLOW">Allowed</option>
      <option value="BLOCK">Blocked</option>
    </select>
  </div>
);

/**
 * Add exception: pick one or more employees, then set check in anywhere and/or
 * multiple check-ins. Only the rules changed from "No change" are sent.
 */
const AddExceptionDialog = ({ onClose }) => {
  const user = useCurrentUser();
  const [picked, setPicked] = useState([]);
  const [anywhere, setAnywhere] = useState("");
  const [multi, setMulti] = useState("");
  const [visits, setVisits] = useState("");
  const [error, setError] = useState(null);
  const update = useUpdateEmployeeCheckInSettings();

  const save = () => {
    if (!picked.length) return setError("Pick at least one employee.");
    if (!anywhere && !multi && !visits)
      return setError("Change at least one rule.");
    setError(null);
    update.mutate(
      {
        employeeIds: picked.map((e) => e.id),
        ...(anywhere && { checkInAnywhere: anywhere }),
        ...(multi && { multipleCheckIns: multi }),
        ...(visits && { clientVisits: visits }),
      },
      {
        onSuccess: () => {
          toast.success(
            `Saved for ${picked.length} employee${picked.length === 1 ? "" : "s"}.`,
          );
          onClose();
        },
        onError: (err) =>
          setError(getApiErrorMessage(err, "Could not save the exception.")),
      },
    );
  };

  return (
    <Dialog
      title="Add exception"
      subtitle="Allow or block a rule for specific employees, whatever their role says."
      onClose={onClose}
      busy={update.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={update.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={save}
            disabled={update.isPending}
          >
            {update.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      {error && <div className="alert alert-danger fs-085 py-2">{error}</div>}
      <label htmlFor="exc-employee" className="profile-label">
        Employees <span className="text-danger">*</span>
      </label>
      <EmployeePicker
        id="exc-employee"
        companyId={user.companyId}
        value={null}
        onChange={(emp) =>
          emp &&
          setPicked((list) =>
            list.some((x) => x.id === emp.id) ? list : [...list, emp],
          )
        }
        excludeId={picked.map((e) => e.id)}
        placeholder="Search name or employee number"
        autoFocus
      />
      <div className="d-flex flex-wrap gap-1 mt-2 mb-3">
        {picked.length === 0 ? (
          <span className="fs-075 text-slate-500">
            Pick employees one by one; they are added here.
          </span>
        ) : (
          picked.map((e) => (
            <span key={e.id} className="chip">
              {employeeName(e)}
              <button
                type="button"
                className="chip-remove"
                onClick={() =>
                  setPicked((list) => list.filter((x) => x.id !== e.id))
                }
                aria-label={`Remove ${employeeName(e)}`}
              >
                <i className="bi bi-x"></i>
              </button>
            </span>
          ))
        )}
      </div>
      <div className="row g-3">
        <RuleSelect
          id="exc-anywhere"
          label="Check in anywhere"
          value={anywhere}
          onChange={setAnywhere}
        />
        <RuleSelect
          id="exc-multi"
          label="Multiple check-ins"
          value={multi}
          onChange={setMulti}
        />
        <RuleSelect
          id="exc-visits"
          label="Client visits"
          value={visits}
          onChange={setVisits}
        />
      </div>
      <p className="fs-075 text-slate-500 mt-2 mb-0">
        Role default = follow the employee&apos;s role (Roles tab). Check-ins
        outside the branch radius are flagged for review.
      </p>
    </Dialog>
  );
};

/** Employees: tag "check in anywhere" / "multiple check-ins", in bulk. */
const EmployeesTab = () => {
  const canModify = useCanModify("attendance", { company: true }); // view-only: no bulk changes
  const [tag, setTag] = useState("");
  const [branchId, setBranchId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const query = useEmployeeCheckInSettings({
    tag,
    branchId,
    search,
    page,
    limit,
  });
  const update = useUpdateEmployeeCheckInSettings();
  const [adding, setAdding] = useState(false);
  // Both rules back to the role: the row leaves the list
  const resetRow = (r) =>
    update.mutate(
      {
        employeeIds: [r.employeeId],
        checkInAnywhere: "INHERIT",
        multipleCheckIns: "INHERIT",
      },
      {
        onSuccess: () => toast.success(`${r.name} follows the role again.`),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not reset the employee.")),
      },
    );
  const rows = query.data?.data || EMPTY;
  const allOnPage =
    rows.length > 0 && rows.every((r) => selected.has(r.employeeId));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) =>
        allOnPage ? next.delete(r.employeeId) : next.add(r.employeeId),
      );
      return next;
    });

  const apply = (changes, label) =>
    update.mutate(
      { employeeIds: [...selected], ...changes },
      {
        onSuccess: () => {
          toast.success(
            `${label} for ${selected.size} employee${selected.size === 1 ? "" : "s"}.`,
          );
          setSelected(new Set());
        },
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, "Could not update the employees."),
          ),
      },
    );

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="fs-085 text-slate-600 mb-0">
          Override the role setting for specific employees.
        </p>
        {canModify && (
          <button
            type="button"
            className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
            onClick={() => setAdding(true)}
          >
            <i className="bi bi-plus-lg me-2"></i>Add exception
          </button>
        )}
      </div>
      <div className="att-filters mb-3">
        <select
          className="status-select"
          value={tag}
          onChange={(e) => {
            setTag(e.target.value);
            setPage(1);
          }}
          aria-label="Tag"
        >
          <option value="">All employees</option>
          <option value="anywhere">Tagged: check in anywhere</option>
          <option value="multiple">Tagged: multiple check-ins allowed</option>
          <option value="clientVisits">Tagged: client visits</option>
          <option value="blocked">Tagged: something blocked</option>
        </select>
        <div className="position-relative att-filter-search">
          <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
          <input
            type="search"
            className="filter-input w-100 ps-5"
            placeholder="Name or employee no."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search"
          />
        </div>
        <div className="att-filter-branch">
          <BranchSelect
            value={branchId}
            onChange={(v) => {
              setBranchId(v || "");
              setPage(1);
            }}
            id="settings-branch"
          />
        </div>
      </div>

      {selected.size > 0 && canModify && (
        <div className="att-bulk-bar" role="region" aria-label="Bulk actions">
          <strong className="fs-085">{selected.size} selected</strong>
          <div className="d-flex flex-wrap gap-2">
            <ModeSelect
              id="bulk-anywhere"
              label="Check in anywhere"
              disabled={update.isPending}
              onPick={(mode) =>
                apply(
                  { checkInAnywhere: mode },
                  `Check in anywhere ${MODE_DONE[mode]}`,
                )
              }
            />
            <ModeSelect
              id="bulk-multi"
              label="Multiple check-ins"
              disabled={update.isPending}
              onPick={(mode) =>
                apply(
                  { multipleCheckIns: mode },
                  `Multiple check-ins ${MODE_DONE[mode]}`,
                )
              }
            />
            <ModeSelect
              id="bulk-visits"
              label="Client visits"
              disabled={update.isPending}
              onPick={(mode) =>
                apply(
                  { clientVisits: mode },
                  `Client visits ${MODE_DONE[mode]}`,
                )
              }
            />
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="glass-table-card">
        {query.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(query.error, "Could not load employees.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0 att-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3 att-col-expand">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allOnPage}
                        onChange={togglePage}
                        aria-label="Select all on this page"
                      />
                    </th>
                    <th className="py-3">Employee</th>
                    <th className="py-3">Branch</th>
                    <th className="py-3 text-center">Check in anywhere</th>
                    <th className="py-3 text-center">Multiple check-ins</th>
                    <th className="py-3 text-center">Client visits</th>
                    <th className="py-3">Location consent</th>
                    <th className="py-3">Updated</th>
                    <th className="py-3 pe-4 text-end"></th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {rows.map((r) => {
                    const consent = CONSENT[String(r.locationConsent ?? null)];
                    return (
                      <tr
                        key={r.employeeId}
                        className={
                          selected.has(r.employeeId) ? "att-row-selected" : ""
                        }
                      >
                        <td className="ps-4">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected.has(r.employeeId)}
                            onChange={() => toggle(r.employeeId)}
                            aria-label={`Select ${r.name}`}
                          />
                        </td>
                        <td>
                          <div className="emp-name-text">{r.name}</div>
                          <div className="emp-sub-text">{r.employeeNumber}</div>
                        </td>
                        <td>{r.branchName || "—"}</td>
                        <td className="text-center">
                          <ModeCell
                            mode={r.checkInAnywhere}
                            effective={r.canCheckInAnywhere}
                          />
                        </td>
                        <td className="text-center">
                          <ModeCell
                            mode={r.multipleCheckIns}
                            effective={r.allowMultipleCheckIns}
                          />
                        </td>
                        <td className="text-center">
                          <ModeCell mode={r.clientVisits || "INHERIT"} />
                        </td>
                        <td>
                          <span
                            className={`status-pill status-pill--${consent.tone}`}
                          >
                            {consent.label}
                          </span>
                        </td>
                        <td
                          className="pe-4 text-nowrap"
                          title={formatDateTime(r.updatedAt)}
                        >
                          {r.updatedAt ? fromNow(r.updatedAt) : "—"}
                        </td>
                        <td className="pe-4 text-end text-nowrap">
                          {canModify && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light border"
                              onClick={() => resetRow(r)}
                              disabled={update.isPending}
                              title="Both rules back to the role default"
                            >
                              Reset to role default
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!query.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="approval-empty">
                        <i className="bi bi-people"></i>
                        {tag || search || branchId
                          ? "No exceptions match these filters."
                          : "No exceptions yet. Everyone follows their role's setting (Roles tab). Use Add exception to allow or block someone individually."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {query.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={limit}
              total={query.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </>
        )}
      </div>
      {adding && <AddExceptionDialog onClose={() => setAdding(false)} />}
    </>
  );
};

/** Role defaults: multiple check-ins, check in anywhere and client visits for everyone with the role (unless set per employee). */
const RolesTab = () => {
  const canModify = useCanModify("attendance", { company: true });
  const query = useRoleCheckInSettings();
  const update = useUpdateRoleCheckInSetting();
  const roles = query.data || EMPTY;

  const toggle = (role, field, label) =>
    update.mutate(
      { roleId: role.id, [field]: !role[field] },
      {
        onSuccess: () =>
          toast.success(
            `${role.title}: ${label} ${role[field] ? "off" : "on"}.`,
          ),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not update the role.")),
      },
    );

  return (
    <div className="glass-table-card">
      {query.isError ? (
        <div className="approval-empty text-danger">
          <i className="bi bi-exclamation-triangle"></i>
          {getApiErrorMessage(query.error, "Could not load roles.")}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table mb-0 att-table">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Role</th>
                <th className="py-3">Multiple check-ins</th>
                <th className="py-3">
                  Check in anywhere
                  <div className="fs-070 fw-normal text-slate-500">
                    Outside the branch radius; flagged for review
                  </div>
                </th>
                <th className="py-3 pe-4">
                  Client visits
                  <div className="fs-070 fw-normal text-slate-500">
                    Can file visits to clients for approval
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {roles.map((r) => (
                <tr key={r.id}>
                  <td className="ps-4 fw-semibold">{r.title}</td>
                  {[
                    ["allowMultipleCheckIns", "multiple check-ins", "multi"],
                    ["canCheckInAnywhere", "check in anywhere", "anywhere"],
                    ["canFileClientVisit", "client visits", "visits"],
                  ].map(([field, label, key], i) => (
                    <td key={field} className={i === 2 ? "pe-4" : ""}>
                      <div className="form-check form-switch m-0">
                        <input
                          id={`role-${key}-${r.id}`}
                          type="checkbox"
                          role="switch"
                          className="form-check-input"
                          checked={!!r[field]}
                          onChange={() => toggle(r, field, label)}
                          disabled={update.isPending || !canModify}
                          aria-label={`${r.title}: ${label}`}
                        />
                        <label
                          htmlFor={`role-${key}-${r.id}`}
                          className="form-check-label fs-080"
                        >
                          {r[field] ? "On" : "Off"}
                        </label>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
              {!query.isLoading && roles.length === 0 && (
                <tr>
                  <td colSpan={4} className="approval-empty">
                    No roles.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Admin/HR: who may check in anywhere, and who gets multiple check-ins. */
const AttendanceSettingsPage = () => {
  // HR-level (attendance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("attendance");
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "roles" ? "roles" : "employees";

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="att-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can change
            check-in settings.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-3">
          <h1 className="fw-bold text-dark m-0 page-title">
            Attendance Settings
          </h1>
          <p className="text-muted small mb-0">
            Who can check in outside the branch radius, and who can check in
            several times a day (field visits). Set a default per role; set an
            employee to Allowed or Blocked to override their role.
          </p>
        </div>

        <div className="alert alert-info fs-085 d-flex gap-2" role="note">
          <i className="bi bi-info-circle mt-1"></i>
          <div>
            The branch radius rule only works for branches that have a location
            set. Employees of branches without a location aren't
            distance-checked. Set branch locations under{" "}
            <Link to="/branch">Branches</Link>.
          </div>
        </div>

        <div className="approval-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "employees"}
            className={`approval-tab ${tab === "employees" ? "active" : ""}`}
            onClick={() => setParams({}, { replace: true })}
          >
            <i className="bi bi-people"></i> Employees
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "roles"}
            className={`approval-tab ${tab === "roles" ? "active" : ""}`}
            onClick={() => setParams({ tab: "roles" }, { replace: true })}
          >
            <i className="bi bi-person-badge"></i> Roles
          </button>
        </div>

        {tab === "employees" ? <EmployeesTab /> : <RolesTab />}
      </div>
    </Layout>
  );
};

export default AttendanceSettingsPage;
